import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { api, setToken, getToken, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import {
  LogOut,
  Save,
  Upload,
  Trash2,
  Plus,
  RefreshCw,
  Image as ImageIcon,
  Mail,
} from "lucide-react";

// ---- helpers ----
const asBilingual = (v) => {
  if (v == null) return { en: "", es: "" };
  if (typeof v === "string") return { en: v, es: v };
  return { en: v.en || "", es: v.es || "" };
};

// -------------- Login --------------
function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("admin@cuquito.com");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setToken(data.access_token);
      onLogin(data);
      toast.success("Welcome, Admin");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6">
      <div className="absolute inset-0 grid-blueprint opacity-30" />
      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        onSubmit={submit}
        className="relative w-full max-w-md border border-white/10 p-10 bg-[#0d0d10]"
        data-testid="admin-login-form"
      >
        <p className="num-tick text-[#1E7FFF] mb-4">[ ADMIN · 001 ]</p>
        <h1 className="font-display uppercase text-4xl md:text-5xl leading-none mb-8">
          Restricted <br />
          <span className="text-[#1E7FFF]">access</span>
        </h1>
        <label className="block mb-6">
          <span className="num-tick text-white/50 block mb-2">Email</span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-transparent border-b border-white/20 py-3 focus:border-[#1E7FFF] focus:outline-none"
            data-testid="admin-login-email"
          />
        </label>
        <label className="block mb-8">
          <span className="num-tick text-white/50 block mb-2">Password</span>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-transparent border-b border-white/20 py-3 focus:border-[#1E7FFF] focus:outline-none"
            data-testid="admin-login-password"
          />
        </label>
        <button
          type="submit"
          disabled={loading}
          className="btn-primary w-full justify-center"
          data-testid="admin-login-submit"
        >
          {loading ? "Verifying..." : "Sign in"}
        </button>
      </motion.form>
    </div>
  );
}

// -------------- Field helpers --------------
function TextField({ label, value, onChange, multiline, testId }) {
  return (
    <label className="block mb-4">
      <span className="num-tick text-white/50 block mb-2">{label}</span>
      {multiline ? (
        <textarea
          rows={3}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-[#0d0d10] border border-white/10 p-3 text-white focus:border-[#1E7FFF] focus:outline-none resize-none font-body"
          data-testid={testId}
        />
      ) : (
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-[#0d0d10] border border-white/10 p-3 text-white focus:border-[#1E7FFF] focus:outline-none font-body"
          data-testid={testId}
        />
      )}
    </label>
  );
}

// Bilingual field: side-by-side EN / ES inputs
function BiField({ label, value, onChange, multiline, testId }) {
  const v = asBilingual(value);
  const set = (k) => (val) => onChange({ ...v, [k]: val });
  return (
    <div className="mb-4" data-testid={testId}>
      <span className="num-tick text-white/50 block mb-2">{label}</span>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <span className="num-tick text-[#1E7FFF] text-[10px] block mb-1">EN</span>
          {multiline ? (
            <textarea
              rows={3}
              value={v.en}
              onChange={(e) => set("en")(e.target.value)}
              className="w-full bg-[#0d0d10] border border-white/10 p-3 text-white focus:border-[#1E7FFF] focus:outline-none resize-none font-body"
              data-testid={testId ? `${testId}-en` : undefined}
            />
          ) : (
            <input
              type="text"
              value={v.en}
              onChange={(e) => set("en")(e.target.value)}
              className="w-full bg-[#0d0d10] border border-white/10 p-3 text-white focus:border-[#1E7FFF] focus:outline-none font-body"
              data-testid={testId ? `${testId}-en` : undefined}
            />
          )}
        </div>
        <div>
          <span className="num-tick text-white/60 text-[10px] block mb-1">ES</span>
          {multiline ? (
            <textarea
              rows={3}
              value={v.es}
              onChange={(e) => set("es")(e.target.value)}
              className="w-full bg-[#0d0d10] border border-white/10 p-3 text-white focus:border-[#1E7FFF] focus:outline-none resize-none font-body"
              data-testid={testId ? `${testId}-es` : undefined}
            />
          ) : (
            <input
              type="text"
              value={v.es}
              onChange={(e) => set("es")(e.target.value)}
              className="w-full bg-[#0d0d10] border border-white/10 p-3 text-white focus:border-[#1E7FFF] focus:outline-none font-body"
              data-testid={testId ? `${testId}-es` : undefined}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function ImageField({ label, url, onChange, testId }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="mb-6" data-testid={testId}>
      <span className="num-tick text-white/50 block mb-2">{label}</span>
      <div className="flex items-start gap-4">
        <div className="w-32 h-24 border border-white/10 bg-black flex items-center justify-center overflow-hidden">
          {url ? (
            <img src={mediaUrl(url)} alt="" className="w-full h-full object-cover" />
          ) : (
            <ImageIcon className="text-white/30" />
          )}
        </div>
        <div className="flex-1 space-y-2">
          <input
            type="text"
            value={url || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="URL or upload file"
            className="w-full bg-[#0d0d10] border border-white/10 p-2 text-white text-sm focus:border-[#1E7FFF] focus:outline-none"
          />
          <label className="btn-ghost inline-flex cursor-pointer text-[11px] py-2 px-3">
            <Upload size={12} />
            {uploading ? "Uploading..." : "Upload file"}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => upload(e.target.files?.[0])}
            />
          </label>
        </div>
      </div>
    </div>
  );
}

// -------------- Sections editors --------------
function BrandEditor({ data, patch }) {
  const b = data.brand || {};
  const upd = (k) => (v) => patch({ brand: { ...b, [k]: v } });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <TextField label="Name (brand)" value={b.name} onChange={upd("name")} testId="admin-brand-name" />
      <TextField label="Phone" value={b.phone} onChange={upd("phone")} testId="admin-brand-phone" />
      <TextField label="WhatsApp (digits only)" value={b.whatsapp} onChange={upd("whatsapp")} />
      <TextField label="WhatsApp (display)" value={b.whatsapp_display} onChange={upd("whatsapp_display")} />
      <TextField label="Founded year" value={b.since} onChange={upd("since")} />
      <TextField label="Email" value={b.email} onChange={upd("email")} />
      <div className="md:col-span-2">
        <BiField label="Tagline (EN / ES)" value={b.tagline} onChange={upd("tagline")} />
      </div>
      <div className="md:col-span-2">
        <BiField label="Location (EN / ES)" value={b.location} onChange={upd("location")} />
      </div>
      <div className="md:col-span-2">
        <ImageField label="Logo" url={b.logo} onChange={upd("logo")} testId="admin-brand-logo" />
      </div>
    </div>
  );
}

function HeroEditor({ data, patch }) {
  const h = data.hero || {};
  const upd = (k) => (v) => patch({ hero: { ...h, [k]: v } });
  return (
    <div className="space-y-3">
      <BiField label="Eyebrow" value={h.eyebrow} onChange={upd("eyebrow")} testId="admin-hero-eyebrow" />
      <BiField label="Line 1" value={h.line1} onChange={upd("line1")} testId="admin-hero-line1" />
      <BiField label="Line 2 (outlined)" value={h.line2} onChange={upd("line2")} testId="admin-hero-line2" />
      <BiField label="Line 3 (blue)" value={h.line3} onChange={upd("line3")} testId="admin-hero-line3" />
      <BiField label="Line 4" value={h.line4} onChange={upd("line4")} testId="admin-hero-line4" />
      <BiField label="Sub-copy" multiline value={h.sub} onChange={upd("sub")} />
      <BiField label="Primary CTA" value={h.cta_primary} onChange={upd("cta_primary")} />
      <BiField label="Secondary CTA" value={h.cta_secondary} onChange={upd("cta_secondary")} />
    </div>
  );
}

function ServicesEditor({ data, patch }) {
  const items = data.services || [];
  const setItems = (arr) => patch({ services: arr });
  const update = (i, k, v) => {
    const arr = [...items];
    arr[i] = { ...arr[i], [k]: v };
    setItems(arr);
  };
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));
  const add = () =>
    setItems([
      ...items,
      {
        id: crypto.randomUUID(),
        num: String(items.length + 1).padStart(2, "0"),
        title: { en: "New service", es: "Nuevo servicio" },
        sub: { en: "", es: "" },
        desc: { en: "", es: "" },
        image: "",
      },
    ]);
  return (
    <div className="space-y-4">
      {items.map((s, i) => (
        <div key={s.id || i} className="border border-white/10 p-4 bg-[#0d0d10]" data-testid={`admin-service-${i}`}>
          <div className="flex justify-between items-center mb-3">
            <p className="num-tick text-[#1E7FFF]">{s.num || String(i + 1).padStart(2, "0")}</p>
            <button
              onClick={() => remove(i)}
              className="text-white/40 hover:text-red-400"
              data-testid={`admin-service-remove-${i}`}
            >
              <Trash2 size={16} />
            </button>
          </div>
          <TextField label="Number" value={s.num} onChange={(v) => update(i, "num", v)} />
          <BiField label="Title" value={s.title} onChange={(v) => update(i, "title", v)} />
          <BiField label="Sub-label" value={s.sub} onChange={(v) => update(i, "sub", v)} />
          <BiField label="Description" multiline value={s.desc} onChange={(v) => update(i, "desc", v)} />
          <ImageField label="Image" url={s.image} onChange={(v) => update(i, "image", v)} />
        </div>
      ))}
      <button onClick={add} className="btn-ghost" data-testid="admin-service-add">
        <Plus size={14} /> Add service
      </button>
    </div>
  );
}

function GalleryEditor({ data, patch }) {
  const items = data.gallery || [];
  const setItems = (arr) => patch({ gallery: arr });
  const update = (i, k, v) => {
    const arr = [...items];
    arr[i] = { ...arr[i], [k]: v };
    setItems(arr);
  };
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));
  const add = () =>
    setItems([...items, { id: crypto.randomUUID(), url: "", caption: { en: "", es: "" } }]);
  return (
    <div className="space-y-4">
      {items.map((g, i) => (
        <div key={g.id || i} className="border border-white/10 p-4 bg-[#0d0d10]" data-testid={`admin-gallery-${i}`}>
          <div className="flex justify-between items-center mb-3">
            <p className="num-tick text-[#1E7FFF]">Photo {String(i + 1).padStart(2, "0")}</p>
            <button
              onClick={() => remove(i)}
              className="text-white/40 hover:text-red-400"
              data-testid={`admin-gallery-remove-${i}`}
            >
              <Trash2 size={16} />
            </button>
          </div>
          <BiField label="Caption" value={g.caption} onChange={(v) => update(i, "caption", v)} />
          <ImageField label="Photo" url={g.url} onChange={(v) => update(i, "url", v)} />
        </div>
      ))}
      <button onClick={add} className="btn-ghost" data-testid="admin-gallery-add">
        <Plus size={14} /> Add photo
      </button>
    </div>
  );
}

function ManifestoEditor({ data, patch }) {
  const m = data.manifesto || { chapters: [] };
  const upd = (obj) => patch({ manifesto: { ...m, ...obj } });
  const items = m.chapters || [];
  const setItems = (arr) => upd({ chapters: arr });
  const update = (i, k, v) => {
    const arr = [...items];
    arr[i] = { ...arr[i], [k]: v };
    setItems(arr);
  };
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));
  const add = () => setItems([...items, { num: String(items.length + 1).padStart(2, "0"), title: { en: "", es: "" }, body: { en: "", es: "" } }]);
  return (
    <div>
      <BiField label="Section title" value={m.title} onChange={(v) => upd({ title: v })} />
      <div className="space-y-4 mt-4">
        {items.map((c, i) => (
          <div key={i} className="border border-white/10 p-4 bg-[#0d0d10]">
            <div className="flex justify-between items-center mb-3">
              <p className="num-tick text-[#1E7FFF]">Chapter {c.num}</p>
              <button onClick={() => remove(i)} className="text-white/40 hover:text-red-400">
                <Trash2 size={16} />
              </button>
            </div>
            <TextField label="Number" value={c.num} onChange={(v) => update(i, "num", v)} />
            <BiField label="Title" value={c.title} onChange={(v) => update(i, "title", v)} />
            <BiField label="Body" multiline value={c.body} onChange={(v) => update(i, "body", v)} />
          </div>
        ))}
        <button onClick={add} className="btn-ghost">
          <Plus size={14} /> Add chapter
        </button>
      </div>
    </div>
  );
}

function TestimonialsEditor({ data, patch }) {
  const items = data.testimonials || [];
  const setItems = (arr) => patch({ testimonials: arr });
  const update = (i, k, v) => {
    const arr = [...items];
    arr[i] = { ...arr[i], [k]: v };
    setItems(arr);
  };
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));
  const add = () => setItems([...items, { name: "", role: { en: "", es: "" }, quote: { en: "", es: "" } }]);
  return (
    <div className="space-y-4">
      {items.map((t, i) => (
        <div key={i} className="border border-white/10 p-4 bg-[#0d0d10]">
          <div className="flex justify-between items-center mb-3">
            <p className="num-tick text-[#1E7FFF]">Testimonial {String(i + 1).padStart(2, "0")}</p>
            <button onClick={() => remove(i)} className="text-white/40 hover:text-red-400">
              <Trash2 size={16} />
            </button>
          </div>
          <TextField label="Name" value={t.name} onChange={(v) => update(i, "name", v)} />
          <BiField label="Role / City" value={t.role} onChange={(v) => update(i, "role", v)} />
          <BiField label="Quote" multiline value={t.quote} onChange={(v) => update(i, "quote", v)} />
        </div>
      ))}
      <button onClick={add} className="btn-ghost">
        <Plus size={14} /> Add testimonial
      </button>
    </div>
  );
}

function GeneralEditor({ data, patch }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="md:col-span-2">
        <BiField
          label="Marquee text"
          value={data.marquee}
          onChange={(v) => patch({ marquee: v })}
          testId="admin-marquee"
        />
      </div>
      <TextField
        label="Service area (comma-separated)"
        value={(data.service_area || []).join(", ")}
        onChange={(v) => patch({ service_area: v.split(",").map((s) => s.trim()).filter(Boolean) })}
      />
      <div className="md:col-span-2">
        <BiField
          label="SEO · Title"
          value={data.seo?.title}
          onChange={(v) => patch({ seo: { ...(data.seo || {}), title: v } })}
        />
      </div>
      <div className="md:col-span-2">
        <BiField
          label="SEO · Description"
          multiline
          value={data.seo?.description}
          onChange={(v) => patch({ seo: { ...(data.seo || {}), description: v } })}
        />
      </div>
      <div className="md:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-3">
        {(data.stats || []).map((s, i) => (
          <div key={i} className="border border-white/10 p-3">
            <TextField
              label={`Stat ${i + 1} · Number`}
              value={s.num}
              onChange={(v) => {
                const arr = [...(data.stats || [])];
                arr[i] = { ...arr[i], num: v };
                patch({ stats: arr });
              }}
            />
            <BiField
              label="Label"
              value={s.label}
              onChange={(v) => {
                const arr = [...(data.stats || [])];
                arr[i] = { ...arr[i], label: v };
                patch({ stats: arr });
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function LeadsPanel() {
  const [leads, setLeads] = useState([]);
  const load = () => api.get("/leads").then((r) => setLeads(r.data));
  useEffect(() => {
    load();
  }, []);
  const del = async (id) => {
    await api.delete(`/leads/${id}`);
    load();
    toast.success("Request deleted");
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="num-tick text-white/50">{leads.length} requests</p>
        <button onClick={load} className="btn-ghost text-[11px] py-2 px-3">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>
      <div className="space-y-3">
        {leads.map((l) => (
          <div key={l.id} className="border border-white/10 p-4 bg-[#0d0d10]">
            <div className="flex justify-between items-start flex-wrap gap-3">
              <div>
                <p className="text-white text-lg font-display uppercase">{l.name}</p>
                <p className="num-tick text-white/50">{new Date(l.created_at).toLocaleString()}</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <a href={`tel:${l.phone}`} className="btn-ghost text-[11px] py-2 px-3">
                  📞 {l.phone}
                </a>
                {l.email && (
                  <a href={`mailto:${l.email}`} className="btn-ghost text-[11px] py-2 px-3">
                    <Mail size={12} /> {l.email}
                  </a>
                )}
                <button
                  onClick={() => del(l.id)}
                  className="btn-ghost text-[11px] py-2 px-3 hover:!border-red-400"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
            <div className="mt-3 num-tick text-[#1E7FFF]">SERVICE · {l.service}</div>
            {l.message && (
              <p className="mt-3 text-white/70 font-body text-sm">{l.message}</p>
            )}
          </div>
        ))}
        {leads.length === 0 && (
          <p className="text-white/40 text-sm">No requests yet.</p>
        )}
      </div>
    </div>
  );
}

// -------------- Main dashboard --------------
const TABS = [
  { id: "brand", label: "Brand" },
  { id: "hero", label: "Hero" },
  { id: "services", label: "Services" },
  { id: "manifesto", label: "Manifesto" },
  { id: "gallery", label: "Gallery" },
  { id: "testimonials", label: "Testimonials" },
  { id: "general", label: "General/SEO" },
  { id: "leads", label: "Requests" },
];

export default function Admin() {
  const [user, setUser] = useState(null);
  const [content, setContent] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [tab, setTab] = useState("brand");
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const t = getToken();
    if (!t) return;
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setToken(null));
  }, []);

  useEffect(() => {
    if (!user) return;
    api.get("/content").then((r) => setContent(r.data));
  }, [user]);

  const patch = (obj) => {
    setContent((c) => ({ ...c, ...obj }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/content", { data: content });
      setContent(data);
      setDirty(false);
      toast.success("Changes saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Restore default content? Your current changes will be lost.")) return;
    const { data } = await api.post("/content/reset");
    setContent(data);
    setDirty(false);
    toast.success("Content restored");
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    navigate("/");
  };

  if (!user) return <LoginScreen onLogin={setUser} />;
  if (!content)
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center">
        <p className="num-tick">Loading...</p>
      </div>
    );

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="sticky top-0 z-30 bg-black/95 backdrop-blur-xl border-b border-white/10 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.7)]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-4 flex-wrap border-b border-white/10">
          <div className="flex items-center gap-4">
            <img src={content.brand?.logo} alt="" className="h-9 w-auto" />
            <div>
              <p className="num-tick text-[#1E7FFF]">[ CMS · v2 ]</p>
              <p className="text-white/80 text-sm">Admin panel · Bilingual (EN / ES)</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {dirty && (
              <span className="num-tick text-amber-400">UNSAVED CHANGES</span>
            )}
            <button onClick={reset} className="btn-ghost text-[11px] py-2 px-3" data-testid="admin-reset">
              <RefreshCw size={12} /> Reset
            </button>
            <button
              onClick={save}
              disabled={saving || !dirty}
              className="btn-primary text-[11px] py-2 px-3 disabled:opacity-40"
              data-testid="admin-save"
            >
              <Save size={12} /> {saving ? "Saving..." : "Save"}
            </button>
            <button onClick={logout} className="btn-ghost text-[11px] py-2 px-3" data-testid="admin-logout">
              <LogOut size={12} /> Log out
            </button>
          </div>
        </div>
        <div className="bg-[#0d0d10]">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex gap-1 overflow-x-auto">
            {TABS.map((tab_item) => {
              const active = tab === tab_item.id;
              return (
                <button
                  key={tab_item.id}
                  onClick={() => setTab(tab_item.id)}
                  className={`font-mono-x text-[12px] tracking-[0.18em] uppercase py-4 px-5 border-b-2 transition-all whitespace-nowrap ${
                    active
                      ? "border-[#1E7FFF] text-white bg-[#1E7FFF]/10"
                      : "border-transparent text-white/70 hover:text-white hover:bg-white/[0.04]"
                  }`}
                  data-testid={`admin-tab-${tab_item.id}`}
                >
                  {tab_item.label}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        {tab === "brand" && <BrandEditor data={content} patch={patch} />}
        {tab === "hero" && <HeroEditor data={content} patch={patch} />}
        {tab === "services" && <ServicesEditor data={content} patch={patch} />}
        {tab === "manifesto" && <ManifestoEditor data={content} patch={patch} />}
        {tab === "gallery" && <GalleryEditor data={content} patch={patch} />}
        {tab === "testimonials" && <TestimonialsEditor data={content} patch={patch} />}
        {tab === "general" && <GeneralEditor data={content} patch={patch} />}
        {tab === "leads" && <LeadsPanel />}
      </main>
    </div>
  );
}
